import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  Menu, Car, MessageSquare, Calendar, Award, Users as UsersIcon,
  LogOut, User, Shield, ChevronDown, Home, Newspaper, Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const navLinks = [
  { href: '/', label: 'Ana Sayfa', icon: Home },
  { href: '/forum', label: 'Forum', icon: MessageSquare },
  { href: '/araclar', label: 'Araçlar', icon: Car },
  { href: '/etkinlikler', label: 'Etkinlikler', icon: Calendar },
  { href: '/haberler', label: 'Haberler', icon: Newspaper },
  { href: '/kullanicilar', label: 'Kullanıcılar', icon: UsersIcon },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 sm:gap-2.5 shrink-0 min-w-0" data-testid="nav-logo">
      <div className="w-10 h-10 sm:w-11 sm:h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-sm shrink-0">
        <Car className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
      </div>
      <div className="flex flex-col leading-tight min-w-0">
        <span className="font-extrabold text-lg sm:text-xl text-slate-800 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Mini<span className="text-emerald-600">Oto</span>
        </span>
        <span className="hidden sm:block text-[10px] text-slate-400 font-medium tracking-wide uppercase">
          Mikromobilite Topluluğu
        </span>
      </div>
    </Link>
  );
}

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState('');
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let active = true;
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/messages/unread-count`, { withCredentials: true });
        if (active) setUnread(r.data.unread || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => { active = false; clearInterval(t); };
  }, [user, location.pathname]);

  const handleLogout = async () => {
    await logout();
    toast.success('Çıkış yapıldı');
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/forum?q=${encodeURIComponent(q.trim())}`);
    }
  };

  const avatarUrl = user?.avatar_url
    ? (user.avatar_url.startsWith('http') ? user.avatar_url : `${API}/files/${user.avatar_url}`)
    : '';
  const initials = (user?.display_name || user?.username || 'U').slice(0, 2).toUpperCase();

  const isActive = (href) => href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/95 border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-4">
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace('/', '') || 'home'}-link`}
                  className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors duration-150 ${
                    active ? 'text-emerald-600' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {link.label}
                  {active && (
                    <span className="absolute -bottom-[22px] left-2 right-2 h-0.5 bg-emerald-600 rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Search */}
          <form onSubmit={handleSearch} className="hidden xl:flex items-center flex-1 max-w-xs">
            <div className="relative w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Forumda ara..."
                data-testid="nav-search-input"
                className="pl-9 h-10 rounded-full bg-slate-50 border-slate-200 focus:bg-white"
              />
            </div>
          </form>

          {/* Auth area */}
          <div className="flex items-center gap-2">
            {user && (
              <Link
                to="/mesajlar"
                className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="nav-messages-link"
                aria-label="Mesajlar"
              >
                <MessageSquare className="w-5 h-5 text-slate-600" />
                {unread > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center"
                    data-testid="nav-messages-badge"
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-user-menu"
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
                  >
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={avatarUrl} />
                      <AvatarFallback className="bg-emerald-100 text-emerald-700 text-xs font-bold">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium text-slate-700 hidden sm:block">
                      {user.display_name || user.username}
                    </span>
                    <ChevronDown className="w-3 h-3 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem asChild>
                    <Link to={`/profil/${user._id}`} data-testid="nav-profile-link">
                      <User className="w-4 h-4 mr-2" /> Profilim
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/mesajlar" data-testid="nav-messages-link-dropdown">
                      <MessageSquare className="w-4 h-4 mr-2" /> Mesajlarım
                      {unread > 0 && <span className="ml-auto bg-emerald-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{unread > 99 ? '99+' : unread}</span>}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/garaj" data-testid="nav-garage-link-dropdown">
                      <Car className="w-4 h-4 mr-2" /> Garajım
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/rozetler" data-testid="nav-badges-link">
                      <Award className="w-4 h-4 mr-2" /> Rozetlerim
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" data-testid="nav-admin-link">
                          <Shield className="w-4 h-4 mr-2" /> Admin Paneli
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-btn" className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-full border-slate-200 h-10 px-5" asChild>
                  <Link to="/giris" data-testid="nav-login-btn">Giriş Yap</Link>
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-10 px-5" asChild>
                  <Link to="/kayit" data-testid="nav-register-btn">Kayıt Ol</Link>
                </Button>
              </div>
            )}

            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="nav-mobile-menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col gap-1 mt-6">
                  <form onSubmit={(e) => { handleSearch(e); setMobileOpen(false); }} className="mb-4">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Forumda ara..."
                        className="pl-9 h-10 rounded-full bg-slate-50"
                      />
                    </div>
                  </form>
                  {navLinks.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
                          isActive(link.href) ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {link.label}
                      </Link>
                    );
                  })}
                  {!user && (
                    <div className="flex flex-col gap-2 mt-4 pt-4 border-t">
                      <Button variant="outline" asChild onClick={() => setMobileOpen(false)}>
                        <Link to="/giris">Giriş Yap</Link>
                      </Button>
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" asChild onClick={() => setMobileOpen(false)}>
                        <Link to="/kayit">Kayıt Ol</Link>
                      </Button>
                    </div>
                  )}
                  {user && (
                    <div className="mt-4 pt-4 border-t">
                      <button onClick={() => { handleLogout(); setMobileOpen(false); }}
                        className="flex items-center gap-2 text-red-600 px-4 py-2 w-full text-left">
                        <LogOut className="w-4 h-4" /> Çıkış Yap
                      </button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
